import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useNavigate, useParams } from 'react-router';
import { useBackHref } from '@/hooks/useListLocation';
import { z } from 'zod';
import { graphql } from '@/gql';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';

const TerminalDoc = graphql(/* GraphQL */ `
  query Terminal($id: ID!) {
    terminal(id: $id) {
      id
      name
      poiid
      isActive
    }
  }
`);

const CreateTerminalDoc = graphql(/* GraphQL */ `
  mutation CreateTerminal($input: CreateTerminalInput!) {
    createTerminal(input: $input) {
      id
    }
  }
`);

const UpdateTerminalDoc = graphql(/* GraphQL */ `
  mutation UpdateTerminal($id: ID!, $input: UpdateTerminalInput!) {
    updateTerminal(id: $id, input: $input) {
      id
      name
      isActive
    }
  }
`);

const createSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  poiid: z.string().min(1, 'POIID is required'),
  isActive: z.boolean(),
});

const updateSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  isActive: z.boolean(),
});

export function TerminalEditPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const backHref = useBackHref('/terminals');
  const isNew = !id || id === 'new';

  const { data, loading } = useQuery(TerminalDoc, {
    variables: { id: id ?? '' },
    skip: isNew,
  });

  const [createTerminal, { loading: creating }] = useMutation(CreateTerminalDoc);
  const [updateTerminal, { loading: updating }] = useMutation(UpdateTerminalDoc);

  const terminal = data?.terminal;
  const [name, setName] = useState('');
  const [poiid, setPoiid] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [seededFor, setSeededFor] = useState<string | null>(null);

  if (terminal && terminal.id !== seededFor) {
    setSeededFor(terminal.id);
    setName(terminal.name);
    setPoiid(terminal.poiid);
    setIsActive(terminal.isActive);
  }

  const saving = creating || updating;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) return;
    setErrors({});

    if (isNew) {
      const parsed = createSchema.safeParse({ name, poiid, isActive });
      if (!parsed.success) {
        const fieldErrors: Record<string, string> = {};
        for (const issue of parsed.error.issues) {
          const key = issue.path[0];
          if (typeof key === 'string') fieldErrors[key] = issue.message;
        }
        setErrors(fieldErrors);
        return;
      }
      await createTerminal({ variables: { input: parsed.data } });
      navigate(backHref);
      return;
    }

    const parsed = updateSchema.safeParse({ name, isActive });
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === 'string') fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    if (id) {
      await updateTerminal({ variables: { id, input: parsed.data } });
      navigate(backHref);
    }
  };

  if (!isNew && loading && !data) {
    return <p className="text-sm text-gray-500">Loading terminal…</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {isNew ? 'New terminal' : `Edit ${terminal?.name ?? ''}`}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Payment terminals identified by their POIID for the scoped venue.
        </p>
      </div>

      <FormLayout
        title="Terminal details"
        onSubmit={handleSubmit}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              type="button"
              onClick={() => navigate(backHref)}
            >
              Cancel
            </Button>
            <Button type="submit" id="admin.terminals.save" disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Create terminal' : 'Save terminal'}
            </Button>
          </div>
        }
      >
        <FormSection title="Identity">
          <FormField label="Name" required error={errors.name}>
            <Input
              id="admin.terminals.name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              invalid={Boolean(errors.name)}
              required
            />
          </FormField>
          <FormField
            label="POIID"
            required
            description={
              isNew
                ? 'Immutable identifier supplied by the acquirer.'
                : 'POIID cannot be changed after creation.'
            }
            error={errors.poiid}
          >
            <Input
              id="admin.terminals.poiid"
              value={poiid}
              onChange={(event) => setPoiid(event.target.value)}
              invalid={Boolean(errors.poiid)}
              disabled={!isNew}
              required={isNew}
            />
          </FormField>
        </FormSection>

        <FormSection title="Status">
          <FormField label="Active">
            <Switch
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
              label={isActive ? 'Accepting payments' : 'Inactive'}
            />
          </FormField>
        </FormSection>
      </FormLayout>
    </div>
  );
}
